import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import AgentCard from "./AgentCard";
import Seo, { breadcrumbJsonLd } from "./Seo";
import { ShieldCheck, MapPin, Search, ArrowRight } from "lucide-react";

/** Shared directory page for any partner role (advocate, architect, interior_designer, civil_contractor).
 *  Renders: SEO hero, service-features grid, city filter chip row, verified filter, and cards.
 *
 *  Props:
 *    - role:            partner role key (e.g. "architect")
 *    - roleLabel:       plural human label ("Architects")
 *    - roleLabelSingular ("Architect")
 *    - path:            URL path (e.g. "/architects")
 *    - heroTag:         short tag above H1 (e.g. "Design & Planning")
 *    - heroTitle:       React node — H1 text
 *    - heroSubtitle:    string — supporting sentence
 *    - features:        array of { icon, title, desc }
 *    - jsonLdCategory:  string used in structured-data ("LegalService", "ProfessionalService")
 */
export default function PartnerDirectory({
    role,
    roleLabel,
    roleLabelSingular,
    path,
    heroTag,
    heroTitle,
    heroSubtitle,
    features = [],
    jsonLdCategory = "ProfessionalService",
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [city, setCity] = useState("");
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [q, setQ] = useState("");

    useEffect(() => {
        setLoading(true);
        api.get("/partners", { params: { role } })
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [role]);

    const cities = useMemo(() => {
        const set = new Set();
        items.forEach((i) => { if (i.city) set.add(i.city); });
        return Array.from(set).sort();
    }, [items]);

    const filtered = useMemo(() => {
        return items.filter((i) => {
            if (verifiedOnly && !i.verified) return false;
            if (city && (i.city || "").toLowerCase() !== city.toLowerCase()) return false;
            if (q) {
                const hay = `${i.name || ""} ${i.company_name || ""} ${i.bio || ""} ${i.city || ""}`.toLowerCase();
                if (!hay.includes(q.toLowerCase())) return false;
            }
            return true;
        });
    }, [items, city, verifiedOnly, q]);

    // Structured data — publicised list of professionals in this category
    const jsonLd = useMemo(() => {
        const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://globalpropertymart.com";
        return {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `Verified ${roleLabel} on Global Property Mart`,
            itemListElement: filtered.slice(0, 20).map((p, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                item: {
                    "@type": jsonLdCategory,
                    "@id": `${baseUrl}/partners/${p.id}`,
                    name: p.name || p.company_name,
                    url: `${baseUrl}/partners/${p.id}`,
                    address: p.city ? { "@type": "PostalAddress", addressLocality: p.city, addressCountry: "IN" } : undefined,
                },
            })),
        };
    }, [filtered, roleLabel, jsonLdCategory]);

    return (
        <div className="min-h-screen bg-white">
            <Seo
                title={`Verified ${roleLabel} in Nagpur & across India`}
                description={`Hire ${filtered.length}+ RERA-checked ${roleLabel.toLowerCase()} on Global Property Mart. ${heroSubtitle}`}
                path={path}
                keywords={`${roleLabel.toLowerCase()} in Nagpur, ${roleLabelSingular.toLowerCase()} near me, best ${roleLabel.toLowerCase()} India, verified ${roleLabel.toLowerCase()} Maharashtra`}
                jsonLd={[
                    breadcrumbJsonLd([
                        { name: "Home", url: "/" },
                        { name: "Services", url: "/services" },
                        { name: roleLabel, url: path },
                    ]),
                    jsonLd,
                ]}
            />

            {/* HERO */}
            <section className="text-on-image relative bg-gradient-to-br from-[#001a3d] via-[#002657] to-[#12234b] border-b border-gold-500/10 overflow-hidden">
                <div className="absolute inset-0 grid-pattern opacity-25" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                    <div className="label-overline mb-3">{heroTag}</div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight">
                        {heroTitle}
                    </h1>
                    <p className="text-white/70 text-base sm:text-lg mt-5 max-w-2xl">{heroSubtitle}</p>

                    {/* Trust badges */}
                    <div className="flex flex-wrap gap-3 mt-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-gold-500/20 text-xs text-white/80">
                            <ShieldCheck className="w-3.5 h-3.5 text-gold-500" /> KYC-verified
                        </div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-gold-500/20 text-xs text-white/80">
                            <MapPin className="w-3.5 h-3.5 text-gold-500" /> Pan-India network
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            {features.length > 0 && (
                <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-16">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                        {features.map((f) => {
                            const Icon = f.icon;
                            return (
                                <div key={f.title} className="rounded-2xl bg-navy-800 border border-white/5 p-6 hover:border-gold-500/40 hover-lift transition-colors">
                                    <div className="w-11 h-11 rounded-lg bg-gold-500/10 border border-gold-500/30 flex items-center justify-center mb-4">
                                        <Icon className="w-5 h-5 text-gold-500" />
                                    </div>
                                    <h3 className="font-heading text-lg font-bold text-white mb-2">{f.title}</h3>
                                    <p className="text-sm text-white/65 leading-relaxed">{f.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* FILTERS + DIRECTORY */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-16 sm:pb-24">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                    <div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                            Featured {roleLabel.toLowerCase()}
                        </h2>
                        <p className="text-white/60 text-sm mt-1">
                            {loading ? "Loading directory…" : `${filtered.length} verified profile${filtered.length === 1 ? "" : "s"}`}
                        </p>
                    </div>
                    <Link
                        to="/register"
                        className="text-on-image inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gold-500 text-white text-xs font-bold hover:bg-gold-600"
                        data-testid={`directory-${role}-join-cta`}
                    >
                        Join as {roleLabelSingular} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                <div className="rounded-2xl bg-navy-800 border border-white/5 p-4 sm:p-5 mb-6 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={`Search ${roleLabel.toLowerCase()} by name or city…`}
                            data-testid={`directory-${role}-search`}
                            className="w-full bg-navy-900 border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-gold-500/60 focus:outline-none"
                        />
                    </div>
                    {cities.length > 0 && (
                        <select
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            data-testid={`directory-${role}-city`}
                            className="bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                        >
                            <option value="">All cities</option>
                            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-md border border-white/10 bg-navy-900">
                        <input
                            type="checkbox"
                            checked={verifiedOnly}
                            onChange={(e) => setVerifiedOnly(e.target.checked)}
                            data-testid={`directory-${role}-verified`}
                            className="accent-gold-500"
                        />
                        <span className="text-xs text-white/80 font-semibold">Verified only</span>
                    </label>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                        {[1,2,3].map((i) => <div key={i} className="h-64 rounded-2xl shimmer" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-white/60 rounded-2xl bg-navy-800 border border-white/5">
                        <p className="mb-3">No {roleLabel.toLowerCase()} match your filters yet.</p>
                        <p className="text-xs text-white/40">
                            Are you a {roleLabelSingular.toLowerCase()}?{" "}
                            <Link to="/register" className="text-gold-500 hover:text-gold-300 font-semibold">Join our directory</Link>
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid={`directory-${role}-grid`}>
                        {filtered.map((a) => <AgentCard key={a.id} agent={a} />)}
                    </div>
                )}
            </section>
        </div>
    );
}
